import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CanRole } from '@/lib/local-platform';
import { Clock, Eye } from 'lucide-react';
import { Image } from '@/components/ui/image';

import type { SpeechTopic } from '@shared/api.interface';
import {
  CONTENT_CATEGORY_LABELS,
  SUITABLE_GRADE_LABELS,
  CONTENT_CATEGORY_COLORS,
  SUITABLE_GRADE_COLORS,
} from '@shared/api.interface';

interface TopicListProps {
  topics: SpeechTopic[];
  onViewDetail: (topic: SpeechTopic) => void;
  onEdit?: (topic: SpeechTopic) => void;
}

export function TopicList({ topics, onViewDetail, onEdit }: TopicListProps) {
  if (topics.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 text-6xl">🔍</div>
        <h3 className="text-lg font-medium text-foreground">暂无匹配的主题</h3>
        <p className="text-sm text-muted-foreground">
          尝试调整筛选条件或搜索关键词
        </p>
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4"
      data-ai-section-type="card-list"
    >
      {topics.map((topic) => (
        <Card
          key={topic.id}
          className="group overflow-hidden transition-all hover:shadow-md"
        >
          <CardContent className="p-4">
            {/* 封面图 */}
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg bg-muted">
              {topic.cover_image ? (
                <Image
                  src={topic.cover_image}
                  alt={topic.title}
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  width={300}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                  无封面
                </div>
              )}
            </div>

            {/* 内容区 */}
            <div className="mt-3">
              <h3 className="text-base font-semibold text-foreground line-clamp-1">
                {topic.title}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {/* 分类标签 */}
                <Badge
                  variant="secondary"
                  className={`${CONTENT_CATEGORY_COLORS[topic.content_category].bg} ${CONTENT_CATEGORY_COLORS[topic.content_category].text} border-0 text-xs`}
                >
                  {CONTENT_CATEGORY_LABELS[topic.content_category]}
                </Badge>
                {/* 年级标签 */}
                <Badge
                  variant="secondary"
                  className={`${SUITABLE_GRADE_COLORS[topic.suitable_grade].bg} ${SUITABLE_GRADE_COLORS[topic.suitable_grade].text} border-0 text-xs`}
                >
                  {SUITABLE_GRADE_LABELS[topic.suitable_grade]}
                </Badge>
              </div>
              {/* 时长 */}
              <span className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {topic.estimated_duration}
              </span>
            </div>

            {/* 操作按钮 */}
            <div className="mt-3 flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => onViewDetail(topic)}
                className="flex-1 gap-1"
              >
                <Eye className="h-3.5 w-3.5" />
                查看
              </Button>
              <CanRole roles={['admin']}>
                {onEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEdit(topic)}
                  >
                    编辑
                  </Button>
                )}
              </CanRole>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
